import { useEffect, useMemo, useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import {
  Alert,
  App as AntdApp,
  Avatar,
  Button,
  Card,
  DatePicker,
  Drawer,
  Form,
  Image,
  Input,
  InputNumber,
  Popconfirm,
  Segmented,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  StarFilled,
} from '@ant-design/icons'
import type { Dayjs } from 'dayjs'
import { supabase } from '@/lib/supabase'
import { revalidateNextTag } from '@/lib/revalidate'
import { formatMembershipDuration } from '@/lib/products'
import { formatCST, nowCST, pickerToUTCISO, toCSTDayjs } from '@/lib/time'
import { AdminMobileList } from '@/components/AdminMobileList'
import { AdminRecordActions } from '@/components/AdminRecordActions'
import { useIsMobile } from '@/lib/hooks'

export const Route = createFileRoute('/_authed/products')({
  component: ProductsPage,
})

/* ── 类型 ───────────────────────────────────────────────────── */

interface ProductRow {
  id: string
  type: 'membership'
  name: string
  description: string | null
  image_url: string | null
  price: number | string
  original_price: number | string | null
  currency: string
  duration_days: number | null
  is_permanent: boolean
  promotion_start_at: string | null
  promotion_end_at: string | null
  is_recommended: boolean
  badge: string | null
  stock_limit: number | null
  sales_count: number | string
  is_active: boolean
  sort_order: number
  created_at: string | null
  updated_at: string | null
}

type StatusFilter = 'active' | 'archived' | 'all'

/* ── helpers ────────────────────────────────────────────────── */

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return v
}

function asNumber(v: number | string | null | undefined): number {
  if (v === null || v === undefined) return 0
  return typeof v === 'string' ? Number(v) : v
}

function isPromotionActive(row: ProductRow): boolean {
  if (!row.promotion_start_at && !row.promotion_end_at) return false
  const now = nowCST()
  if (row.promotion_start_at && now.isBefore(row.promotion_start_at))
    return false
  if (row.promotion_end_at && now.isAfter(row.promotion_end_at)) return false
  return true
}

/* ── 主组件 ─────────────────────────────────────────────────── */

function ProductsPage() {
  const qc = useQueryClient()
  const { message } = AntdApp.useApp()

  const [status, setStatus] = useState<StatusFilter>('active')
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounced(search.trim(), 300)

  const [editing, setEditing] = useState<ProductRow | 'new' | null>(null)
  const isMobile = useIsMobile()

  const permanentQuery = useQuery({
    queryKey: ['products', 'permanent'],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('products')
        .select('*')
        .eq('is_permanent', true)
        .order('sort_order', { ascending: true })
      if (error) throw error
      return (data ?? []) as Array<ProductRow>
    },
  })

  const reservedPermanent = permanentQuery.data?.[0] ?? null

  const listQuery = useQuery({
    queryKey: ['products', { status, search: debouncedSearch }],
    placeholderData: keepPreviousData,
    queryFn: async () => {
      // products 表新建，database.types.ts 未重生成；cast 跳过类型

      let q: any = (supabase as any)
        .from('products')
        .select('*')
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false })
      if (status === 'active') q = q.eq('is_active', true)
      if (status === 'archived') q = q.eq('is_active', false)
      if (debouncedSearch) q = q.ilike('name', `%${debouncedSearch}%`)
      const { data, error } = await q
      if (error) throw error
      return (data ?? []) as Array<ProductRow>
    },
  })

  const toggleActiveMutation = useMutation({
    mutationFn: async (params: { id: string; isActive: boolean }) => {
      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('set_product_active', {
          p_id: params.id,
          p_is_active: params.isActive,
          p_reason: null,
        })
      if (error) throw error
      const r = data as { ok: boolean; error?: string }
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return r
    },
    onSuccess: (_, vars) => {
      message.success(vars.isActive ? '已上架' : '已下架')
      qc.invalidateQueries({ queryKey: ['products'] })
      // 顺手触发 C 端 Next 缓存失效（不 await，失败只 warn）
      void revalidateNextTag('products')
      void (supabase as any)
        .schema('admin')
        .rpc('revalidate_cache', { p_tags: ['products'] })
    },
    onError: (e: Error) => message.error(e.message || '操作失败'),
  })

  const columns: ColumnsType<ProductRow> = useMemo(
    () => [
      {
        title: '#',
        dataIndex: 'sort_order',
        width: 56,
        render: (n: number) => (
          <Typography.Text
            type="secondary"
            style={{ fontSize: 12, fontVariantNumeric: 'tabular-nums' }}
          >
            {n}
          </Typography.Text>
        ),
      },
      {
        title: '商品',
        render: (_: unknown, row) => (
          <Space size={10}>
            {row.image_url ? (
              <Image
                src={row.image_url}
                alt=""
                width={40}
                height={40}
                preview={{ mask: false }}
                style={{ objectFit: 'cover', borderRadius: 4 }}
              />
            ) : (
              <Avatar
                shape="square"
                size={40}
                style={{ background: 'var(--ant-color-fill-tertiary)' }}
              >
                {row.name.slice(0, 1)}
              </Avatar>
            )}
            <Space direction="vertical" size={0}>
              <Space size={6}>
                <Typography.Text style={{ fontSize: 13 }}>
                  {row.name}
                </Typography.Text>
                {row.is_permanent ? <Tag color="magenta">永久</Tag> : null}
                {row.is_recommended ? (
                  <Tooltip title="推荐位">
                    <StarFilled style={{ color: '#fadb14' }} />
                  </Tooltip>
                ) : null}
                {row.badge ? <Tag color="volcano">{row.badge}</Tag> : null}
              </Space>
              {row.description ? (
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: 11 }}
                  ellipsis
                >
                  {row.description}
                </Typography.Text>
              ) : null}
            </Space>
          </Space>
        ),
      },
      {
        title: '类型',
        dataIndex: 'type',
        width: 80,
        render: (t: string) => <Tag>{t === 'membership' ? '会员' : t}</Tag>,
      },
      {
        title: '价格',
        width: 130,
        render: (_: unknown, row) => {
          const price = asNumber(row.price)
          const original =
            row.original_price != null ? asNumber(row.original_price) : null
          const showStrike = original !== null && original > price
          return (
            <Space direction="vertical" size={0}>
              <Typography.Text
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                ¥{price.toFixed(2)}
              </Typography.Text>
              {showStrike ? (
                <Typography.Text
                  delete
                  type="secondary"
                  style={{ fontSize: 11, fontVariantNumeric: 'tabular-nums' }}
                >
                  ¥{original.toFixed(2)}
                </Typography.Text>
              ) : null}
            </Space>
          )
        },
      },
      {
        title: '时长',
        width: 90,
        render: (_: unknown, row) => (
          <Typography.Text style={{ fontSize: 13 }}>
            {formatMembershipDuration({
              isPermanent: row.is_permanent,
              durationDays: row.duration_days,
            })}
          </Typography.Text>
        ),
      },
      {
        title: '促销',
        width: 150,
        render: (_: unknown, row) => {
          if (!row.promotion_start_at && !row.promotion_end_at) {
            return (
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                —
              </Typography.Text>
            )
          }
          const active = isPromotionActive(row)
          return (
            <Space direction="vertical" size={0}>
              <Tag
                color={active ? 'success' : 'default'}
                style={{ marginRight: 0 }}
              >
                {active ? '促销中' : '未生效/已结束'}
              </Tag>
              <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                {formatCST(row.promotion_start_at, 'MM-DD HH:mm')}
                {' → '}
                {formatCST(row.promotion_end_at, 'MM-DD HH:mm')}
              </Typography.Text>
            </Space>
          )
        },
      },
      {
        title: '销量',
        dataIndex: 'sales_count',
        width: 80,
        render: (n: number | string) => (
          <Typography.Text
            style={{ fontSize: 13, fontVariantNumeric: 'tabular-nums' }}
          >
            {asNumber(n).toLocaleString('zh-CN')}
          </Typography.Text>
        ),
      },
      {
        title: '状态',
        dataIndex: 'is_active',
        width: 90,
        render: (a: boolean) => (
          <Tag color={a ? 'success' : 'default'}>{a ? '上架' : '下架'}</Tag>
        ),
      },
      {
        title: '操作',
        width: 160,
        fixed: 'right',
        render: (_: unknown, row) => (
          <Space size={4}>
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={(e) => {
                e.stopPropagation()
                setEditing(row)
              }}
            >
              编辑
            </Button>
            <Popconfirm
              title={row.is_active ? '下架该商品？' : '上架该商品？'}
              description={
                row.is_active
                  ? 'C 端将看不到该商品，已存在的订单不受影响。'
                  : row.is_permanent
                    ? '确认售价已改完再上架。占位价未确认时不要打开。'
                    : 'C 端将能看到该商品。'
              }
              onConfirm={() =>
                toggleActiveMutation.mutate({
                  id: row.id,
                  isActive: !row.is_active,
                })
              }
              okText={row.is_active ? '下架' : '上架'}
              cancelText="取消"
              okButtonProps={{ danger: row.is_active }}
            >
              <Button
                type="link"
                size="small"
                danger={row.is_active}
                onClick={(e) => e.stopPropagation()}
              >
                {row.is_active ? '下架' : '上架'}
              </Button>
            </Popconfirm>
          </Space>
        ),
      },
    ],
    [toggleActiveMutation],
  )

  const rows = listQuery.data ?? []

  return (
    <>
      <Card
        title="商品"
        extra={
          <Space>
            <Tooltip title={isMobile ? '刷新' : undefined}>
              <Button
                icon={<ReloadOutlined />}
                aria-label="刷新"
                onClick={() => listQuery.refetch()}
                loading={listQuery.isFetching && !listQuery.isLoading}
              >
                {isMobile ? null : '刷新'}
              </Button>
            </Tooltip>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setEditing('new')}
            >
              新建商品
            </Button>
          </Space>
        }
      >
        {reservedPermanent && !reservedPermanent.is_active ? (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="永久会员套餐已预留，当前下架"
            description="改价后再上架。不要新建一条同名商品。给用户补偿请走用户详情，不要走这里。"
            action={
              <Button
                size="small"
                onClick={() => setEditing(reservedPermanent)}
              >
                改价
              </Button>
            }
          />
        ) : null}

        <Space wrap className="admin-filter-grid" style={{ marginBottom: 16 }}>
          <Segmented<StatusFilter>
            block={isMobile}
            value={status}
            onChange={(v) => setStatus(v)}
            options={[
              { label: '上架中', value: 'active' },
              { label: '已下架', value: 'archived' },
              { label: '全部', value: 'all' },
            ]}
          />
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="搜商品名"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 240 }}
          />
        </Space>

        <div className="admin-table-wrap">
          <Table<ProductRow>
            rowKey="id"
            size="middle"
            loading={listQuery.isLoading}
            columns={columns}
            dataSource={rows}
            onRow={(row) => ({
              onClick: () => setEditing(row),
              style: { cursor: 'pointer' },
            })}
            scroll={{ x: 1200 }}
            pagination={{
              pageSize: 50,
              showSizeChanger: false,
              showTotal: (total) => `共 ${total} 条`,
            }}
          />
        </div>

        <AdminMobileList
          rows={rows}
          rowKey={(row) => row.id}
          loading={listQuery.isLoading || listQuery.isFetching}
          renderItem={(row) => {
            const price = asNumber(row.price)
            const original =
              row.original_price != null ? asNumber(row.original_price) : null
            const showStrike = original !== null && original > price

            return (
              <article
                className="admin-mobile-record"
                role="button"
                tabIndex={0}
                onClick={() => setEditing(row)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    setEditing(row)
                  }
                }}
              >
                <div className="admin-mobile-record__head">
                  <div className="admin-mobile-record__lead">
                    {row.image_url ? (
                      <Image
                        src={row.image_url}
                        alt=""
                        width={40}
                        height={40}
                        preview={false}
                        style={{ objectFit: 'cover', borderRadius: 4 }}
                      />
                    ) : (
                      <Avatar
                        shape="square"
                        size={40}
                        style={{
                          background: 'var(--ant-color-fill-tertiary)',
                        }}
                      >
                        {row.name.slice(0, 1)}
                      </Avatar>
                    )}
                    <div className="admin-mobile-record__title">
                      <Typography.Text>{row.name}</Typography.Text>
                      {row.description ? (
                        <div className="admin-mobile-record__meta">
                          <span>{row.description}</span>
                        </div>
                      ) : null}
                    </div>
                  </div>
                  <Tag color={row.is_active ? 'success' : 'default'}>
                    {row.is_active ? '上架' : '下架'}
                  </Tag>
                </div>
                <div className="admin-mobile-record__badges">
                  <Tag>会员</Tag>
                  {row.is_permanent ? <Tag color="magenta">永久</Tag> : null}
                  {row.is_recommended ? <Tag color="gold">推荐</Tag> : null}
                  {row.badge ? <Tag color="volcano">{row.badge}</Tag> : null}
                  {isPromotionActive(row) ? (
                    <Tag color="success">促销中</Tag>
                  ) : null}
                </div>
                <div className="admin-mobile-record__stats products-mobile-card__stats">
                  <span className="products-mobile-card__price">
                    ¥{price.toFixed(2)}
                    {showStrike ? (
                      <Typography.Text delete type="secondary">
                        ¥{original.toFixed(2)}
                      </Typography.Text>
                    ) : null}
                  </span>
                  <span>
                    {formatMembershipDuration({
                      isPermanent: row.is_permanent,
                      durationDays: row.duration_days,
                    })}
                  </span>
                  <span>
                    销量 {asNumber(row.sales_count).toLocaleString('zh-CN')}
                  </span>
                  <span>排序 {row.sort_order}</span>
                </div>
                <AdminRecordActions
                  actions={[
                    {
                      key: 'toggle',
                      label: row.is_active ? '下架' : '上架',
                      danger: row.is_active,
                      loading:
                        toggleActiveMutation.isPending &&
                        toggleActiveMutation.variables?.id === row.id,
                      confirm: {
                        title: row.is_active ? '下架该商品？' : '上架该商品？',
                        description: row.is_active
                          ? 'C 端将看不到该商品，已存在的订单不受影响。'
                          : row.is_permanent
                            ? '确认售价已改完再上架。占位价未确认时不要打开。'
                            : 'C 端将能看到该商品。',
                        okText: row.is_active ? '下架' : '上架',
                      },
                      onClick: () =>
                        toggleActiveMutation.mutate({
                          id: row.id,
                          isActive: !row.is_active,
                        }),
                    },
                    {
                      key: 'edit',
                      label: '编辑',
                      icon: <EditOutlined />,
                      onClick: () => setEditing(row),
                    },
                  ]}
                />
              </article>
            )
          }}
        />
      </Card>

      <EditDrawer
        open={editing !== null}
        row={editing === 'new' ? null : editing}
        reservedPermanentId={reservedPermanent?.id ?? null}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null)
          qc.invalidateQueries({ queryKey: ['products'] })
          qc.invalidateQueries({ queryKey: ['products', 'permanent'] })
        }}
      />
    </>
  )
}

/* ── 编辑抽屉（新建/编辑共用）─────────────────────────────────── */

interface FormValues {
  type: 'membership'
  name: string
  description: string | null
  image_url: string | null
  price: number
  original_price: number | null
  currency: string
  is_permanent: boolean
  duration_days: number | null
  promotion_range: [Dayjs, Dayjs] | null
  is_recommended: boolean
  badge: string | null
  stock_limit: number | null
  sort_order: number
  reason: string
}

function EditDrawer({
  open,
  row,
  reservedPermanentId,
  onClose,
  onSaved,
}: {
  open: boolean
  row: ProductRow | null
  reservedPermanentId: string | null
  onClose: () => void
  onSaved: () => void
}) {
  const { message, modal } = AntdApp.useApp()
  const isMobile = useIsMobile()
  const [form] = Form.useForm<FormValues>()
  const isNew = !row
  const isPermanent = Form.useWatch('is_permanent', form) === true
  const creatingDuplicatePermanent =
    isNew && isPermanent && reservedPermanentId != null
  const demotingPermanent = !isNew && !!row?.is_permanent && !isPermanent

  useEffect(() => {
    if (!open) return
    if (row) {
      form.setFieldsValue({
        type: row.type,
        name: row.name,
        description: row.description ?? '',
        image_url: row.image_url ?? '',
        price: asNumber(row.price),
        original_price:
          row.original_price != null ? asNumber(row.original_price) : null,
        currency: row.currency,
        is_permanent: row.is_permanent === true,
        duration_days: row.is_permanent ? null : (row.duration_days ?? 30),
        promotion_range:
          row.promotion_start_at && row.promotion_end_at
            ? [
                toCSTDayjs(row.promotion_start_at)!,
                toCSTDayjs(row.promotion_end_at)!,
              ]
            : null,
        is_recommended: row.is_recommended,
        badge: row.badge ?? '',
        stock_limit: row.stock_limit,
        sort_order: row.sort_order,
        reason: '',
      })
    } else {
      form.resetFields()
      form.setFieldsValue({
        type: 'membership',
        currency: 'CNY',
        is_permanent: false,
        duration_days: 30,
        is_recommended: false,
        sort_order: 0,
      })
    }
  }, [open, row, form])

  const imageUrl = Form.useWatch('image_url', form)

  const saveMutation = useMutation({
    mutationFn: async (values: FormValues) => {
      if (isNew && values.is_permanent && reservedPermanentId) {
        throw new Error('duplicate_permanent_product')
      }

      const { data, error } = await (supabase as any)
        .schema('admin')
        .rpc('upsert_product', {
          p_id: row?.id ?? null,
          p_type: values.type,
          p_name: values.name.trim(),
          p_description: values.description?.trim() || null,
          p_image_url: values.image_url?.trim() || null,
          p_price: values.price,
          p_original_price: values.original_price ?? null,
          p_currency: values.currency,
          p_duration_days: values.is_permanent ? null : values.duration_days,
          p_is_permanent: values.is_permanent,
          p_promotion_start_at: pickerToUTCISO(values.promotion_range?.[0]),
          p_promotion_end_at: pickerToUTCISO(values.promotion_range?.[1]),
          p_is_recommended: values.is_recommended,
          p_badge: values.badge?.trim() || null,
          p_stock_limit: values.stock_limit ?? null,
          p_sort_order: values.sort_order,
          p_reason: values.reason?.trim() || null,
        })
      if (error) throw error
      const r = data as { ok: boolean; error?: string; id?: string }
      if (!r.ok) throw new Error(r.error ?? 'unknown')
      return r
    },
    onSuccess: () => {
      message.success(isNew ? '已创建' : '已保存')
      onSaved()
      void revalidateNextTag('products')
      void (supabase as any)
        .schema('admin')
        .rpc('revalidate_cache', { p_tags: ['products'] })
    },
    onError: (e: Error) => {
      const msgMap: Record<string, string> = {
        name_required: '商品名不能为空',
        invalid_price: '价格不合法',
        invalid_original_price: '原价不合法',
        duration_days_required: '限时会员必须填写时长',
        invalid_permanent_duration: '永久套餐不能填写时长',
        duplicate_permanent_product: '已有永久会员商品，请编辑那一行',
        invalid_promotion_range: '促销开始时间必须早于结束时间',
        invalid_stock_limit: '库存不合法',
        invalid_sort_order: '排序值不合法',
        not_found: '商品不存在（可能已被删除）',
        forbidden: '无权限',
      }
      message.error(msgMap[e.message] || e.message || '保存失败')
    },
  })

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={isMobile ? '100%' : 640}
      title={isNew ? '新建商品' : `编辑 · ${row.name}`}
      destroyOnHidden
      footer={
        <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={onClose}>取消</Button>
          <Button
            type="primary"
            loading={saveMutation.isPending}
            disabled={creatingDuplicatePermanent}
            onClick={() => {
              if (demotingPermanent) {
                modal.confirm({
                  title: '降级为限时套餐？',
                  content:
                    '将失去永久商品身份，必须填写天数。已有订单不受影响。',
                  okText: '确认降级',
                  okButtonProps: { danger: true },
                  cancelText: '取消',
                  onOk: () => form.submit(),
                })
                return
              }
              form.submit()
            }}
          >
            {isNew ? '创建' : '保存'}
          </Button>
        </Space>
      }
    >
      <Form<FormValues>
        form={form}
        layout="vertical"
        requiredMark
        onFinish={(v) => saveMutation.mutate(v)}
      >
        {row?.is_permanent ? (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="可购买的永久套餐"
            description="改价后上架。给用户补偿请走用户详情的「设为永久」，不要走这里。"
          />
        ) : null}
        {isNew ? (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="可购买的永久套餐已在库里（默认下架）"
            description="请编辑那一行改价，不要再建一条同名商品。"
          />
        ) : null}
        {creatingDuplicatePermanent ? (
          <Alert
            type="error"
            showIcon
            style={{ marginBottom: 16 }}
            message="已有永久会员商品"
            description="关闭后去编辑已预留的那一行。"
          />
        ) : null}

        <Form.Item label="类型" name="type" required>
          <Select
            disabled
            options={[{ value: 'membership', label: '会员（membership）' }]}
          />
        </Form.Item>

        <Form.Item
          label="名称"
          name="name"
          rules={[
            { required: true, message: '请输入名称' },
            { max: 100, message: '最长 100 字' },
          ]}
        >
          <Input placeholder="如：月卡 / 季卡 / 年卡 / 永久会员" />
        </Form.Item>

        <Form.Item label="副标题 / 描述" name="description">
          <Input.TextArea
            autoSize={{ minRows: 2, maxRows: 4 }}
            placeholder="一句营销文案，C 端商品卡片副标题"
          />
        </Form.Item>

        <Form.Item label="封面图 URL" name="image_url">
          <Input placeholder="https://..." />
        </Form.Item>
        {imageUrl ? (
          <div style={{ marginTop: -8, marginBottom: 16 }}>
            <Image
              src={imageUrl}
              alt="预览"
              width={120}
              height={120}
              style={{ objectFit: 'cover', borderRadius: 4 }}
            />
          </div>
        ) : null}

        <Space style={{ display: 'flex', marginBottom: 0 }}>
          <Form.Item
            label="售价（¥）"
            name="price"
            rules={[{ required: true, message: '请输入售价' }]}
            style={{ flex: 1 }}
          >
            <InputNumber
              min={0}
              step={0.01}
              precision={2}
              style={{ width: '100%' }}
              placeholder="当前售价"
            />
          </Form.Item>
          <Form.Item
            label="原价（划线，可选）"
            name="original_price"
            style={{ flex: 1 }}
          >
            <InputNumber
              min={0}
              step={0.01}
              precision={2}
              style={{ width: '100%' }}
              placeholder="不填 = 不显示划线"
            />
          </Form.Item>
        </Space>

        <Space style={{ display: 'flex', marginBottom: 0 }} align="start">
          <Form.Item label="币种" name="currency" style={{ flex: 1 }}>
            <Select
              disabled
              options={[{ value: 'CNY', label: 'CNY · 人民币' }]}
            />
          </Form.Item>
          <Form.Item
            label="永久套餐"
            name="is_permanent"
            valuePropName="checked"
            style={{ flex: 1 }}
            tooltip="勾选后时长必须为空。库里已有 seed，新建请勿再勾。"
          >
            <Switch
              onChange={(checked) => {
                form.setFieldValue('duration_days', checked ? null : 30)
              }}
            />
          </Form.Item>
        </Space>

        <Form.Item
          label="会员时长（天）"
          name="duration_days"
          rules={
            isPermanent
              ? []
              : [{ required: true, message: '限时会员必须填写时长' }]
          }
          extra={isPermanent ? '永久商品不填天数，与时长互斥。' : undefined}
        >
          <InputNumber
            min={1}
            disabled={isPermanent}
            placeholder={isPermanent ? '永久，无需天数' : undefined}
            style={{ width: '100%' }}
          />
        </Form.Item>

        <Form.Item label="促销时段（可选）" name="promotion_range">
          <DatePicker.RangePicker
            showTime={{ format: 'HH:mm' }}
            format="YYYY-MM-DD HH:mm"
            style={{ width: '100%' }}
            placeholder={['促销开始', '促销结束']}
          />
        </Form.Item>

        <Space size="large" style={{ marginBottom: 16 }} wrap>
          <Form.Item
            label="推荐位"
            name="is_recommended"
            valuePropName="checked"
            style={{ marginBottom: 0 }}
            tooltip="C 端首选高亮"
          >
            <Switch />
          </Form.Item>
          <Form.Item
            label="文案标 badge"
            name="badge"
            style={{ marginBottom: 0, minWidth: 200 }}
          >
            <Input placeholder="如：超值 / 限时" maxLength={20} />
          </Form.Item>
        </Space>

        <Space style={{ display: 'flex', marginBottom: 0 }} align="end">
          <Form.Item
            label="库存上限（可选，仅展示用）"
            name="stock_limit"
            style={{ flex: 1 }}
          >
            <InputNumber
              min={0}
              style={{ width: '100%' }}
              placeholder="无限不填"
            />
          </Form.Item>
          <Form.Item
            label="排序"
            name="sort_order"
            tooltip="数字越小越靠前"
            style={{ flex: 1 }}
          >
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Space>

        <Form.Item label="备注（可选，会写入审计日志）" name="reason">
          <Input.TextArea
            autoSize={{ minRows: 2, maxRows: 3 }}
            maxLength={500}
            showCount
            placeholder="不填将记 admin_create_product / admin_update_product"
          />
        </Form.Item>
      </Form>
    </Drawer>
  )
}
